import { printHtml } from "@/components/inventory/shelfPrint";
import { resolveImageUrl } from "@/lib/api";
import { jsPDF } from "jspdf";
import { barcodeWidths } from "@/lib/code128";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const TAG_SIZES = {
  dk1202: { w: 3.9, h: 2.4, scale: 0.72, label: '2.4" × 3.9"' },
  s4x6: { w: 4, h: 6, scale: 1.05, label: '4" × 6" Shipping Label' },
  letter: { w: 8.5, h: 11, scale: 1.9, label: 'Letter — 8.5" × 11"' },
};

const fmtDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso || "";
};
const num = (v) => (v == null || v === "" ? "" : Number(v).toLocaleString());

const productLine = (job) => [job.productName, job.designName, job.casePack ? `${Number(job.casePack)}-Pack` : ""].filter(Boolean).join(" · ");

// ---------- UPC-A encoding (95 modules). Falls back to Code128 for non-UPC values. ----------
const UPC_L = ["0001101", "0011001", "0010011", "0111101", "0100011", "0110001", "0101111", "0111011", "0110111", "0001011"];
const upcModules = (code) => {
  let d = String(code || "").replace(/\D/g, "");
  if (d.length === 11) {
    const s = d.split("").reduce((a, c, i) => a + Number(c) * (i % 2 === 0 ? 3 : 1), 0);
    d += String((10 - (s % 10)) % 10);
  }
  if (d.length !== 12) return null;
  let bits = "101";
  for (let i = 0; i < 6; i++) bits += UPC_L[+d[i]];
  bits += "01010";
  for (let i = 6; i < 12; i++) bits += [...UPC_L[+d[i]]].map((b) => (b === "1" ? "0" : "1")).join("");
  return { bits: bits + "101", digits: d };
};
const widthsToBits = (widths) => widths.map((w, i) => (i % 2 === 0 ? "1" : "0").repeat(w)).join("");
const isImg = (url) => /\.(png|jpe?g)(\?|$)/i.test(url || "");
const upcEncoding = (upc) => {
  if (!upc) return null;
  const enc = upcModules(upc);
  if (enc) return enc;
  try { return { bits: widthsToBits(barcodeWidths(String(upc))), digits: String(upc) }; } catch { return null; }
};

// ---------- HTML print (2 identical tags per pallet: 1A,1B,2A,2B,…) ----------
export const printPalletTags = (job) => {
  const s = TAG_SIZES[job.size] || TAG_SIZES.dk1202;
  const k = s.scale;
  const enc = upcEncoding(job.upc);
  const imgUrl = isImg(job.barcodeUrl) ? resolveImageUrl(job.barcodeUrl) : "";
  const railSvg = imgUrl
    ? `<div style="position:relative;width:${0.88 * k}in;height:100%;overflow:hidden;"><img src="${esc(imgUrl)}" alt="upc" style="position:absolute;top:50%;left:50%;width:${s.h - 0.34 * k}in;height:${0.82 * k}in;object-fit:contain;transform:translate(-50%,-50%) rotate(90deg);" /></div>`
    : enc
      ? `<svg viewBox="0 0 40 ${enc.bits.length}" preserveAspectRatio="none" style="width:${0.26 * k}in;height:100%;">${[...enc.bits].map((b, i) => (b === "1" ? `<rect x="0" y="${i}" width="40" height="1.06" fill="#000"/>` : "")).join("")}</svg>`
      : "";
  const css = `
    .ptag * { box-sizing: border-box; margin: 0; }
    .ptag { font-family: 'Helvetica Neue', Arial, sans-serif; color: #000; background: #fff; width: ${s.w}in; height: ${s.h}in; }
    .ptag .tag { width: 100%; height: 100%; padding: ${0.1 * k}in ${0.12 * k}in; display: flex; gap: ${0.08 * k}in; border: 1px solid #000; overflow: hidden; }
    .ptag .main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
    .ptag .prod { font-size: ${13 * k}px; font-weight: 800; line-height: 1.12; text-align: center; }
    .ptag .row { display: flex; justify-content: space-between; gap: ${8 * k}px; font-size: ${10.5 * k}px; margin-top: ${8 * k}px; }
    .ptag .row b { font-weight: 800; }
    .ptag .pallet { margin-top: auto; font-size: ${10 * k}px; font-weight: 800; text-align: center; border-top: 1px solid #000; padding-top: ${2 * k}px; }
    .ptag .rail { display: flex; align-items: stretch; gap: ${2 * k}px; }
    .ptag .railnum { writing-mode: vertical-rl; transform: rotate(180deg); font-size: ${9 * k}px; font-weight: 700; letter-spacing: 0.08em; text-align: center; font-variant-numeric: tabular-nums; }`;
  const tags = [];
  for (const p of job.pallets) {
    const body = `<div class="tag">
      <div class="main">
        <div class="prod">${esc(productLine(job))}</div>
        <div class="row"><span>Material #: <b>${esc(job.material)}</b></span><span>Batch #: <b>${esc(job.batch)}</b></span></div>
        <div class="row"><span>Quantity: <b>${esc(num(p.cases))} CS${p.units ? ` (${esc(num(p.units))} units)` : ""}</b></span><span>Prod. Date: <b>${esc(fmtDate(job.productionDate))}</b></span></div>
        <div class="row"><span>UPC Code: <b>${esc(job.upc || "—")}</b></span></div>
        <div class="pallet">Pallet ${p.pallet_seq} of ${p.pallet_total}</div>
      </div>
      ${imgUrl ? `<div class="rail">${railSvg}</div>` : enc ? `<div class="rail">${railSvg}<div class="railnum">${esc(enc.digits)}</div></div>` : ""}
    </div>`;
    tags.push(body, body);
  }
  const html = `<html><head><title>Pallet Tags — ${esc(job.productName)}</title><style>
    @page { size: ${s.w}in ${s.h}in; margin: 0; }
    html, body { width: ${s.w}in; margin: 0; }
    .ptag { page-break-after: always; }
    .ptag:last-child { page-break-after: auto; }
    ${css}
  </style></head><body>${tags.map((b) => `<div class="ptag">${b}</div>`).join("")}</body></html>`;
  printHtml(html);
};

// ---------- PDF (pure jsPDF vector — do NOT switch to html2canvas) ----------
const loadImage = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = url;
});

const rotateImg90 = (img) => {
  const c = document.createElement("canvas");
  c.width = img.naturalHeight;
  c.height = img.naturalWidth;
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return { data: c.toDataURL("image/png"), w: c.width, h: c.height };
};

export const downloadPalletTags = async (job) => {
  const s = TAG_SIZES[job.size] || TAG_SIZES.dk1202;
  const k = s.scale;
  const orientation = s.w > s.h ? "landscape" : "portrait";
  const pdf = new jsPDF({ unit: "in", format: [s.w, s.h], orientation });
  const pt = (px) => px * 0.75 * k;
  const m = 0.12 * k;
  const enc = upcEncoding(job.upc);
  let upcImg = null;
  if (isImg(job.barcodeUrl)) {
    const raw = await loadImage(resolveImageUrl(job.barcodeUrl));
    if (raw) upcImg = rotateImg90(raw);
  }
  const maxLen = s.h - 2 * (m + 0.06 * k);
  const imgThick = upcImg ? Math.min(0.8 * k, maxLen / (upcImg.h / upcImg.w)) : 0;
  const railW = upcImg ? imgThick + 0.08 * k : enc ? 0.44 * k : 0;
  const Wc = s.w - 2 * m - railW;
  let first = true;
  for (const p of job.pallets) {
    for (let copy = 0; copy < 2; copy++) {
      if (!first) pdf.addPage([s.w, s.h], orientation);
      first = false;
      pdf.setDrawColor(0).setFillColor(0).setLineWidth(0.01).rect(0.02, 0.02, s.w - 0.04, s.h - 0.04);
      let y = m + 0.14 * k;
      pdf.setFont("helvetica", "bold").setFontSize(pt(13));
      pdf.text(productLine(job), m + Wc / 2, y, { align: "center", maxWidth: Wc });
      y += 0.26 * k;
      const row = (l1, v1, l2, v2) => {
        pdf.setFont("helvetica", "normal").setFontSize(pt(10.5));
        pdf.text(`${l1}: `, m, y);
        pdf.setFont("helvetica", "bold");
        pdf.text(String(v1 ?? ""), m + pdf.getTextWidth(`${l1}:  `), y);
        if (l2) {
          pdf.setFont("helvetica", "bold");
          const vw = pdf.getTextWidth(String(v2 ?? ""));
          pdf.text(String(v2 ?? ""), m + Wc - vw, y);
          pdf.setFont("helvetica", "normal");
          pdf.text(`${l2}: `, m + Wc - vw - pdf.getTextWidth(`${l2}:  `), y);
        }
        y += 0.24 * k;
      };
      row("Material #", job.material, "Batch #", job.batch);
      row("Quantity", `${num(p.cases)} CS${p.units ? ` (${num(p.units)} units)` : ""}`, "Prod. Date", fmtDate(job.productionDate));
      row("UPC Code", job.upc || "—", "", "");
      // vertical UPC barcode + number down the right edge
      if (upcImg) {
        const len = Math.min(maxLen, imgThick * (upcImg.h / upcImg.w));
        const bx = s.w - m - railW + 0.04 * k;
        pdf.addImage(upcImg.data, "PNG", bx, (s.h - len) / 2, imgThick, len);
      } else if (enc) {
        const yTop = m + 0.06 * k;
        const len = s.h - 2 * (m + 0.06 * k);
        const mod = len / enc.bits.length;
        const bx = s.w - m - railW + 0.04 * k;
        const bThick = 0.24 * k;
        [...enc.bits].forEach((b, i) => {
          if (b === "1") pdf.rect(bx, yTop + i * mod, bThick, mod + 0.004, "F");
        });
        pdf.setFont("helvetica", "bold").setFontSize(pt(9));
        pdf.text(String(enc.digits), s.w - m + 0.02 * k, s.h / 2, { angle: 90, align: "center" });
      }
      const by = s.h - m - 0.02 * k;
      pdf.setLineWidth(0.008).line(m, by - 0.14 * k, m + Wc, by - 0.14 * k);
      pdf.setFont("helvetica", "bold").setFontSize(pt(10));
      pdf.text(`Pallet ${p.pallet_seq} of ${p.pallet_total}`, m + Wc / 2, by, { align: "center" });
    }
  }
  const name = `pallet-tags-${(job.productName || "product").replace(/[^a-z0-9]+/gi, "-")}-${(job.batch || "").replace(/[^a-z0-9]+/gi, "-")}`.toLowerCase();
  pdf.save(`${name}.pdf`);
};
